/**
 * Serviços › Importação Brasíndice — tela nativa do AURA (ADR 0012, MODULO_AURA.md §15 B.2).
 *
 * O módulo roda em container próprio (knoxviana/aura-brasindice); esta página
 * chama a API dele direto do navegador com o JWT do AURA (services/brasindiceAPI).
 *
 * Organização, decidida com o Victor: uma linha por edição do Brasíndice com
 * "N de 9 baixados · M importados" e a situação (Importado / Parcial / Pendente);
 * clicar abre os 9 arquivos e o botão de importar. Pesquisa de item no topo.
 * Agendamento numa aba própria. Nada de SQL nem parâmetro: isso é do modo
 * técnico, que fica no próprio módulo.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Pill, Search, RefreshCw, ChevronRight, ChevronDown, CheckCircle2, Clock, AlertTriangle,
  Database, CalendarDays, Play, X, History, Lock, LayoutDashboard, Download,
} from "lucide-react";
import toast from "react-hot-toast";
import { brasindiceAPI, servicoBrasindice } from "../../services/brasindiceAPI";

const T = {
  bg: "#f5f7fa", card: "#ffffff", ink: "#2c3e50", inkSoft: "#7f8c8d",
  line: "#e8e8e8", teal: "var(--brand, #20a490)", tealDark: "#1a8a79", tealSoft: "#e8f6f4",
  risco: "#e74c3c", riscoSoft: "#fdecea", ambar: "#f39c12", ambarSoft: "#fef5e7",
  azul: "#3498db", azulSoft: "#eaf4fc", verde: "#27ae60", verdeSoft: "#e9f7ef",
};
const MONO = "'SF Mono','JetBrains Mono',Consolas,monospace";
const fmtN = (v) => Number(v || 0).toLocaleString("pt-BR");
const fmtR = (v) => (v == null ? "—" : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const pad = (n) => String(n).padStart(2, "0");
const DIAS_SEMANA = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
function parseData(x) {
  if (!x) return null;
  let m = String(x).match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[ T,]+(\d{2}):(\d{2}))?/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0));
  m = String(x).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0));
  const d = new Date(x); return Number.isNaN(d.getTime()) ? null : d;
}
const fmtDia = (x) => { const d = parseData(x); return d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : (x || "—"); };
const fmtData = (x) => { const d = parseData(x); return d ? `${fmtDia(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}` : (x || "—"); };
const fmtExtenso = (x) => { const d = parseData(x); return d ? `${DIAS_SEMANA[d.getDay()]}, ${fmtDia(d)} às ${pad(d.getHours())}:${pad(d.getMinutes())}` : "—"; };

const Card = ({ children, style }) => (
  <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 12, padding: 20, ...style }}>{children}</div>
);
const TH = { padding: "10px 12px", fontWeight: 600, textAlign: "left", fontSize: 12, textTransform: "uppercase", letterSpacing: ".04em", color: T.inkSoft, borderBottom: `1px solid ${T.line}` };
const TD = { padding: "12px 12px", color: T.ink, verticalAlign: "middle", borderTop: `1px solid ${T.line}` };
const Botao = ({ children, onClick, cor = T.teal, disabled, titulo, sec }) => (
  <button onClick={onClick} disabled={disabled} title={titulo}
    style={{ background: sec ? "#fff" : cor, color: sec ? T.tealDark : "#fff", border: `1px solid ${sec ? "#cfe9e4" : cor}`,
      borderRadius: 8, padding: "8px 14px", fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1,
      display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13.5 }}>
    {children}
  </button>
);

/* ── selos de situação ─────────────────────────────────────────────────── */
const SIT = {
  importado: { rot: "Importado", cor: "#fff", fundo: T.teal, dica: "Os 9 arquivos desta edição estão gravados no Soul de simulação." },
  parcial: { rot: "Parcial", cor: "#856404", fundo: T.ambarSoft, dica: "Alguns arquivos já estão no Soul, outros ainda não." },
  pendente: { rot: "Pendente", cor: "#4b5563", fundo: "#eef0f3", dica: "Nenhum arquivo desta edição foi gravado no Soul ainda." },
};
const ARQ = {
  ausente: { rot: "Não baixado", cor: "#64748b", fundo: "#f1f5f9", dica: "O download ainda não trouxe este arquivo. Use Disparar agora em Agendamento." },
  pendente: { rot: "Aguardando importação", cor: "#1565c0", fundo: T.azulSoft, dica: "Baixado. Será testado e gravado ao clicar em Importar." },
  validado: { rot: "Aguardando importação", cor: "#1565c0", fundo: T.azulSoft, dica: "Baixado e conferido. Será testado e gravado ao clicar em Importar." },
  sql_gerado: { rot: "Aguardando importação", cor: "#1565c0", fundo: T.azulSoft, dica: "Baixado e conferido. Será testado e gravado ao clicar em Importar." },
  testado: { rot: "Testado, pronto", cor: "#155724", fundo: T.verdeSoft, dica: "Já passou no teste do Oracle. Será gravado ao clicar em Importar." },
  importado: { rot: "Importado", cor: "#fff", fundo: T.teal, dica: "Gravado no Soul de simulação." },
  erro: { rot: "Com problema", cor: "#721c24", fundo: T.riscoSoft, dica: "Este arquivo não passou. Os outros podem ser importados; avise a TI." },
};
const Selo = ({ rot, cor, fundo, dica, icone }) => (
  <span title={dica} style={{ background: fundo, color: cor, borderRadius: 20, padding: "4px 11px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 5, cursor: "help" }}>
    {icone}{rot}
  </span>
);
const SeloArquivo = ({ a }) => {
  if (a.status !== "importado" && a.status !== "ausente" && a.anterior_pendente) {
    return <Selo rot={`Aguarda a ${a.anterior_pendente}`} cor="#856404" fundo={T.ambarSoft} icone={<Clock size={13} />}
      dica={`A edição ${a.anterior_pendente} deste tipo ainda não foi importada. As edições entram em ordem.`} />;
  }
  const s = ARQ[a.status] || ARQ.pendente;
  return <Selo {...s} icone={a.status === "importado" ? <CheckCircle2 size={13} /> : a.status === "erro" ? <AlertTriangle size={13} /> : null} />;
};

/* ── situação de uma edição (mesma regra da tela do módulo) ────────────── */
function situacaoEdicao(ed) {
  const presentes = ed.arquivos.filter((a) => a.existe || a.status === "importado");
  const importados = presentes.filter((a) => a.status === "importado");
  const erros = presentes.filter((a) => a.status === "erro");
  const restantes = presentes.filter((a) => a.status !== "importado" && a.status !== "erro");
  if (!presentes.length) return { tipo: "atencao", texto: "Nenhum arquivo baixado ainda.", pode: false };
  if (importados.length === ed.total) return { tipo: "ok", texto: `Importada no Soul: ${ed.total} arquivos gravados.`, pode: false };
  const bloqueados = restantes.filter((a) => a.anterior_pendente);
  if (restantes.length && bloqueados.length === restantes.length) {
    const ant = [...new Set(bloqueados.map((a) => a.anterior_pendente))].sort().join(", ");
    return { tipo: "atencao", texto: `Aguardando a edição ${ant}: as edições entram em ordem. Importe a ${ant} primeiro.`, pode: false };
  }
  if (erros.length) return { tipo: "atencao", texto: `${erros.length} arquivo(s) com problema: ${erros.map((a) => a.categoria).join(", ")}. Os demais podem ser importados; avise a TI.`, pode: restantes.length > 0 };
  if (importados.length + ed.baixados < ed.total) return { tipo: "atencao", texto: `Edição incompleta: ${ed.baixados} de ${ed.total} arquivos baixados. Rode o download em Agendamento.`, pode: restantes.length > 0 };
  return { tipo: "pronta", texto: `Pronta para importar: ${restantes.length} arquivo(s)${importados.length ? `, ${importados.length} já gravado(s)` : ""}.`, pode: restantes.length > 0 };
}

/* ── progresso da importação de uma edição ────────────────────────────── */
function Progresso({ st }) {
  if (!st || st.inexistente) return null;
  const FASE = { iniciando: "iniciando", testando: "testando no Oracle (rollback)", gravando: "gravando (commit)", concluido: "concluído" };
  const pct = st.total ? Math.round((100 * (st.concluidos || 0)) / st.total) : 0;
  return (
    <div style={{ background: T.tealSoft, border: "1px solid #cfe9e4", borderRadius: 10, padding: "12px 16px", marginTop: 12, fontSize: 13.5 }}>
      {st.em_andamento
        ? <span><RefreshCw size={14} style={{ verticalAlign: "text-bottom", marginRight: 6 }} />{FASE[st.fase] || st.fase}{st.atual ? ` · ${st.atual}` : ""} · {st.concluidos || 0} de {st.total} arquivos</span>
        : (st.ok ? <b style={{ color: T.verde }}>Importação concluída: {st.gravados} arquivo(s) gravados.</b>
                 : <b style={{ color: T.risco }}>Importação terminou com {st.falhas} falha(s). {st.gravados} gravados. Avise a TI sobre os arquivos marcados.</b>)}
      <div style={{ height: 8, background: "#fff", borderRadius: 4, marginTop: 8, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: T.verde, transition: "width .4s" }} />
      </div>
      <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 4 }}>
        {Object.entries(st.arquivos || {}).map(([k, v]) => (
          <div key={k} style={{ fontSize: 13 }}>
            {v.status === "importado" ? <CheckCircle2 size={13} color={T.verde} /> : v.status === "erro" ? <AlertTriangle size={13} color={T.risco} /> : <Clock size={13} color={T.ambar} />}
            {" "}{k} <span style={{ color: T.inkSoft }}>{v.msg || ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── linha do tempo de um arquivo (modal) ─────────────────────────────── */
function HistoricoArquivo({ categoria, onFechar }) {
  const [d, setD] = useState(null);
  useEffect(() => { brasindiceAPI.historicoArquivo(categoria).then(setD).catch((e) => toast.error(e.message)); }, [categoria]);
  const soul = d?.soul || {};
  return (
    <div onClick={onFechar} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "min(1100px, 100%)", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.line}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div><div style={{ fontWeight: 700, fontSize: 16 }}>{categoria}: linha do tempo</div>
            <div style={{ fontSize: 13, color: T.inkSoft }}>Edição a edição: o que aconteceu no módulo e o que está no Soul.</div></div>
          <button onClick={onFechar} style={{ background: "none", border: "none", cursor: "pointer", color: T.inkSoft }}><X size={20} /></button>
        </div>
        <div style={{ overflow: "auto", padding: "0 20px 16px" }}>
          {!d && <div style={{ padding: 24, color: T.inkSoft }}>Carregando…</div>}
          {d && (
            <>
              <h4 style={{ margin: "16px 0 8px", fontSize: 14 }}>No Soul (simulação)</h4>
              {soul.erro ? <div style={{ color: T.ambar }}>{soul.erro}</div> : soul.cargas?.length ? (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr><th style={TH}>Edição</th><th style={TH}>Carga</th><th style={TH}>Quando</th><th style={TH}>Usuário</th><th style={{ ...TH, textAlign: "right" }}>Linhas</th><th style={{ ...TH, textAlign: "right" }}>Valores</th></tr></thead>
                  <tbody>{soul.cargas.map((c) => (
                    <tr key={c.log} style={{ textDecoration: c.cancelada_por ? "line-through" : "none", color: c.cancelada_por ? T.inkSoft : T.ink }}>
                      <td style={TD}>{c.edicao || "—"}</td><td style={TD}>{c.log}{c.cancelada_por ? " (cancelada)" : ""}</td><td style={TD}>{fmtData(c.quando)}</td>
                      <td style={TD}>{c.usuario || "—"}</td><td style={{ ...TD, textAlign: "right" }}>{fmtN(c.linhas)}</td><td style={{ ...TD, textAlign: "right" }}>{fmtN(c.valores)}</td>
                    </tr>))}</tbody>
                </table>) : <div style={{ color: T.inkSoft }}>Nenhuma carga deste arquivo no Soul.</div>}
              <h4 style={{ margin: "20px 0 8px", fontSize: 14 }}>No módulo</h4>
              {d.eventos.length ? (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr><th style={TH}>Edição</th><th style={TH}>Quando</th><th style={TH}>Evento</th><th style={TH}>Detalhe</th></tr></thead>
                  <tbody>{d.eventos.slice(0, 80).map((h, i) => (
                    <tr key={i}><td style={TD}>{h.edicao}</td><td style={{ ...TD, whiteSpace: "nowrap" }}>{fmtData(h.quando)}</td><td style={TD}>{h.evento}</td><td style={{ ...TD, color: T.inkSoft }}>{h.detalhe || ""}</td></tr>))}</tbody>
                </table>) : <div style={{ color: T.inkSoft }}>Nenhum evento registrado.</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── detalhe de uma edição (linha aberta) ─────────────────────────────── */
function DetalheEdicao({ ed, oracleOn, onImportar, progresso, onHistorico }) {
  const sit = situacaoEdicao(ed);
  const cor = sit.tipo === "ok" ? T.verde : sit.tipo === "atencao" ? T.risco : T.tealDark;
  const podeClicar = oracleOn && sit.pode;
  return (
    <div style={{ padding: "4px 12px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ color: cor, fontSize: 15, fontWeight: 600 }}>{sit.texto}</div>
        <Botao cor={T.verde} disabled={!podeClicar} onClick={() => onImportar(ed)}
          titulo={!oracleOn ? "A execução no Oracle está desligada no módulo." : (!sit.pode ? "Nada a importar nesta edição." : "Testa no Oracle o que ainda não foi testado e grava os arquivos que passaram, um por um.")}>
          <Database size={15} /> Importar edição no Soul
        </Botao>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "4px 28px", marginTop: 12 }}>
        {ed.arquivos.map((a) => (
          <div key={a.categoria} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: `1px solid ${T.line}` }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{a.categoria}</div>
              <div style={{ fontSize: 12, color: T.inkSoft }}>
                {a.linhas ? `${fmtN(a.linhas)} linhas` : ""}
                {a.status === "importado" ? `${a.linhas ? " · " : ""}gravado${a.importado_em ? " em " + fmtDia(a.importado_em) : ""}${a.log ? " · carga " + a.log : ""}${a.origem === "soul" ? " · pelo Soul" : ""}` : ""}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <SeloArquivo a={a} />
              <button onClick={() => onHistorico(a.categoria)} title="Linha do tempo deste arquivo: edição a edição, aqui e no Soul."
                style={{ background: "none", border: "none", cursor: "pointer", color: T.inkSoft, padding: 2 }}><History size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      <Progresso st={progresso} />
      {ed.historico?.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ color: T.inkSoft, fontSize: 13, cursor: "pointer" }}>Últimos acontecimentos ({ed.historico.length})</summary>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, marginTop: 6 }}>
            <tbody>{ed.historico.map((h, i) => (
              <tr key={i}><td style={{ ...TD, padding: "5px 8px", color: T.inkSoft, whiteSpace: "nowrap" }}>{fmtData(h.quando)}</td><td style={{ ...TD, padding: "5px 8px" }}>{h.evento}</td><td style={{ ...TD, padding: "5px 8px" }}>{h.arquivo || "—"}</td><td style={{ ...TD, padding: "5px 8px", color: T.inkSoft }}>{h.detalhe || ""}</td></tr>))}</tbody>
          </table>
        </details>
      )}
    </div>
  );
}

/* ── pesquisa por item ────────────────────────────────────────────────── */
function Pesquisa({ termo, onVoltar }) {
  const [res, setRes] = useState(null);
  const [erro, setErro] = useState(null);
  const [sel, setSel] = useState(null);
  const [soul, setSoul] = useState(null);
  useEffect(() => {
    setRes(null); setSel(null); setSoul(null); setErro(null);
    brasindiceAPI.pesquisarItem(termo).then((d) => { setRes(d); if (d.itens.length === 1) setSel(d.itens[0]); }).catch((e) => setErro(e.message));
  }, [termo]);
  useEffect(() => {
    if (!sel) return;
    setSoul(null);
    brasindiceAPI.historicoSoul(sel.tiss, sel.tuss).then(setSoul).catch((e) => setSoul({ erro: e.message }));
  }, [sel]);
  const TP = { 5: "Brasíndice", 6: "SIMPRO" };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ color: T.inkSoft, fontSize: 13 }}>
          {res ? `${res.itens.length} item(ns), ${res.ocorrencias} ocorrência(s) para "${termo}"${res.truncado ? " (lista limitada; refine a busca)" : ""}. Clique para ver o histórico.` : erro ? "" : "Procurando nos arquivos baixados…"}
        </div>
        <Botao sec onClick={onVoltar}><X size={14} /> Voltar às edições</Botao>
      </div>
      {erro && <div style={{ color: T.risco }}>{erro}</div>}
      {res && !res.itens.length && <Card><span style={{ color: T.inkSoft }}>Nada encontrado para "<b>{termo}</b>" nos arquivos baixados.</span></Card>}
      {res && res.itens.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 1fr) 2fr", gap: 16 }}>
          <div>
            {res.itens.map((it) => (
              <div key={it.tiss} onClick={() => setSel(it)}
                style={{ cursor: "pointer", border: `1px solid ${sel?.tiss === it.tiss ? T.teal : T.line}`, background: sel?.tiss === it.tiss ? T.tealSoft : "#fff", borderRadius: 10, padding: "12px 16px", marginBottom: 8 }}>
                <div style={{ fontWeight: 600 }}>{it.descricao} <span style={{ color: T.inkSoft, fontWeight: 400 }}>{it.apresentacao}</span></div>
                <div style={{ fontSize: 12.5, color: T.inkSoft, fontFamily: MONO }}>TISS {it.tiss} · TUSS {it.tuss || "—"} · EAN {it.ean || "—"}</div>
                <div style={{ fontSize: 12.5, color: T.inkSoft }}>{it.laboratorio} · {[...new Set(it.ocorrencias.map((o) => o.edicao))].sort((a, b) => b - a).slice(0, 6).map((e) => "ed. " + e).join(", ")}</div>
              </div>))}
          </div>
          <div>
            {sel && (
              <>
                <Card style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 700 }}>{sel.descricao} <span style={{ color: T.inkSoft, fontWeight: 400 }}>{sel.apresentacao}</span></div>
                  <div style={{ fontSize: 12.5, color: T.inkSoft, fontFamily: MONO, marginBottom: 10 }}>TISS {sel.tiss} · TUSS {sel.tuss || "—"} · EAN {sel.ean || "—"} · {sel.laboratorio}</div>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Nos arquivos do Brasíndice</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead><tr><th style={TH}>Edição</th><th style={TH}>Arquivo</th><th style={{ ...TH, textAlign: "right" }} title="Preço unitário (fracionado) que a carga grava em VALOR_REF_PROC.">Unitário</th><th style={{ ...TH, textAlign: "right" }} title="Preço da embalagem no arquivo.">Embalagem</th><th style={TH} title="Edição em que o preço deste item mudou pela última vez (NR_EDICAO no Soul).">Último reajuste</th><th style={TH}>Genérico</th></tr></thead>
                    <tbody>{[...sel.ocorrencias].sort((a, b) => (b.edicao - a.edicao) || a.categoria.localeCompare(b.categoria)).map((o, i) => (
                      <tr key={i}><td style={TD}>{o.edicao}</td><td style={TD}>{o.categoria}</td><td style={{ ...TD, textAlign: "right", fontFamily: MONO }}>{fmtR(o.vl_unitario)}</td><td style={{ ...TD, textAlign: "right", color: T.inkSoft, fontFamily: MONO }}>{fmtR(o.vl_medicamento)}{o.qt_fracionamento ? " /" + o.qt_fracionamento : ""}</td><td style={TD}>{o.cd_edicao}</td><td style={TD}>{o.sn_generico || "—"}</td></tr>))}</tbody>
                  </table>
                </Card>
                <Card>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>No Soul (simulação)</div>
                  {!soul && <div style={{ color: T.inkSoft }}>Consultando o Soul (só leitura)…</div>}
                  {soul?.erro && <div style={{ color: T.ambar }}>{soul.erro}</div>}
                  {soul && !soul.erro && !soul.procedimentos.length && <div>Este item não localiza nenhum procedimento cadastrado, nem pelo TISS nem pelo TUSS. A carga não grava preço para ele.</div>}
                  {soul && !soul.erro && soul.procedimentos.length > 0 && (
                    <>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 12 }}>
                        <thead><tr><th style={TH}>Procedimento</th><th style={TH}>Descrição no Soul</th><th style={TH} title="Como o item chega a este procedimento: pelo TISS (10 dígitos), pelo TUSS como código ou pelo TUSS gravado em CD_TISS.">Via</th><th style={TH} title="5 = Brasíndice, 6 = SIMPRO (a carga não toca), vazio = cadastro antigo.">Codif.</th><th style={TH}>Incluído em</th></tr></thead>
                        <tbody>{soul.procedimentos.map((p) => (
                          <tr key={p.cd_procedimento} style={{ color: p.tp_codificacao === "6" ? T.inkSoft : T.ink }}><td style={{ ...TD, fontFamily: MONO }}>{p.cd_procedimento}{p.inativo ? " (inativo)" : ""}</td><td style={TD}>{p.descricao}</td><td style={TD}>{p.via}</td><td style={TD}>{TP[p.tp_codificacao] || p.tp_codificacao || "—"}</td><td style={TD}>{fmtDia(p.incluido_em)}</td></tr>))}</tbody>
                      </table>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Valores de referência ({soul.valores.length})</div>
                      {soul.valores.length ? (
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead><tr><th style={TH}>Procedimento</th><th style={TH}>Tabela</th><th style={TH}>Vigência</th><th style={{ ...TH, textAlign: "right" }}>Valor</th><th style={TH}>Edição</th><th style={TH}>Carga</th><th style={TH}>Gravado em</th></tr></thead>
                          <tbody>{soul.valores.map((v, i) => (
                            <tr key={i} style={{ textDecoration: v.carga_cancelada ? "line-through" : "none", color: v.carga_cancelada ? T.inkSoft : T.ink }}>
                              <td style={{ ...TD, fontFamily: MONO }}>{v.cd_procedimento}</td><td style={TD}>{v.tabela || v.cd_tabela}</td><td style={TD}>{fmtDia(v.vigencia)}</td><td style={{ ...TD, textAlign: "right", fontFamily: MONO }}>{fmtR(v.valor)}</td><td style={TD}>{v.nr_edicao || "—"}</td>
                              <td style={TD}>{v.log ? `${v.log}${v.arquivo ? " · " + v.arquivo : ""}${v.carga_cancelada ? " (cancelada)" : ""}` : (v.usuario || "—")}</td><td style={TD}>{fmtData(v.incluido_em)}</td>
                            </tr>))}</tbody>
                        </table>) : <div style={{ color: T.inkSoft }}>Nenhum valor gravado.</div>}
                    </>
                  )}
                </Card>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── painel de gestão à vista (cartões do topo) ───────────────────────── */
function Painel({ p }) {
  if (!p) return null;
  const r = p.ultima_execucao;
  const sit = p.situacao || {};
  const COR = { ok: T.verde, aguardando: T.ambar, falhou: T.risco, desligado: T.inkSoft };
  const ROT = { ok: "Em dia", aguardando: "Aguardando disparo", falhou: "FALHOU", desligado: "Desligado" };
  const cor = COR[sit.estado] || T.inkSoft;
  const ultimaCor = !r ? T.inkSoft : r.status === "success" ? T.verde : r.status === "skipped" ? T.inkSoft : T.risco;
  const ultimaRot = !r ? "nenhuma ainda" : r.status === "success" ? "concluída" : r.status === "skipped" ? "nada a baixar" : r.status === "running" ? "em andamento" : "falhou";
  const cobertura = p.cobertura || [];
  const atrasados = cobertura.filter((c) => c.edicoes_atrasadas);
  const Cartao = ({ titulo, valor, sub, cor: c, dica }) => (
    <Card style={{ padding: "14px 18px", borderLeft: `3px solid ${c}`, minWidth: 0 }} >
      <div title={dica} style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".04em", color: T.inkSoft, fontWeight: 600, cursor: dica ? "help" : "default" }}>{titulo}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: c, marginTop: 4, lineHeight: 1.2 }}>{valor}</div>
      {sub && <div style={{ fontSize: 12.5, color: T.inkSoft, marginTop: 4 }}>{sub}</div>}
    </Card>
  );
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12, marginBottom: 14 }}>
      <Cartao titulo="Última execução" cor={ultimaCor} dica="Último disparo do download: quando foi, como terminou e o que baixou."
        valor={p.processo_ativo ? "Rodando agora" : (r ? fmtData(r.inicio) : "—")}
        sub={p.processo_ativo ? `desde ${fmtData(p.processo_ativo.inicio)}` : (r ? `${ultimaRot} · ${r.ok} baixado(s), ${r.skip} já existiam, ${r.erro} erro(s) · ${r.duracao_fmt}` : "nenhuma ainda")} />
      <Cartao titulo="Próxima execução" cor={T.tealDark} dica="Próximo disparo previsto pelo agendamento (dias fixos do mês e hora)."
        valor={p.agenda?.ativa ? fmtExtenso(p.proxima_execucao) : "—"}
        sub={p.agenda?.ativa ? `dias ${(p.agenda.dias || []).join(", ")} às ${p.agenda.hora}` : "agendamento desligado"} />
      <Cartao titulo="Situação do agendamento" cor={cor} dica="Em dia: o último disparo previsto aconteceu. FALHOU: passou da hora prevista sem disparo, ou a última execução terminou com erro."
        valor={ROT[sit.estado] || "—"} sub={sit.detalhe || ""} />
      <Cartao titulo="Portal × Soul" cor={atrasados.length ? T.ambar : T.verde} dica="Última edição publicada no Brasíndice e quantos tipos de arquivo ainda não a têm no Soul."
        valor={p.edicao_portal ? `Edição ${p.edicao_portal} no portal` : "—"}
        sub={p.edicao_portal ? (atrasados.length ? `${atrasados.length} de ${cobertura.length} tipos atrasados, até ${p.maior_atraso} edições` : "Soul em dia com o portal") : (p.publicada_portal || "")} />
    </div>
  );
}

/* ── agendamento ──────────────────────────────────────────────────────── */
function Agendamento({ admin }) {
  const [ag, setAg] = useState(null);
  const [auto, setAuto] = useState(null);
  const [dias, setDias] = useState(new Set());
  const [hh, setHh] = useState("08");
  const [mm, setMm] = useState("00");
  const [ativa, setAtiva] = useState(true);
  const timer = useRef(null);
  const carregar = useCallback(() => {
    brasindiceAPI.agenda().then((d) => {
      setAg(d); setDias(new Set(d.dias || [])); setAtiva(!!d.ativa);
      const [h, m] = (d.hora || "08:00").split(":"); setHh(h); setMm(m);
      if (d.processo_ativo && !timer.current) timer.current = setInterval(() => carregar(), 5000);
      if (!d.processo_ativo && timer.current) { clearInterval(timer.current); timer.current = null; }
    }).catch((e) => toast.error(e.message));
    brasindiceAPI.automacao().then(setAuto).catch(() => setAuto(null));
  }, []);
  useEffect(() => { carregar(); return () => { if (timer.current) clearInterval(timer.current); }; }, [carregar]);
  const salvar = () => brasindiceAPI.salvarAgenda({ ativa, dias: [...dias].sort((a, b) => a - b), hora: `${hh}:${mm}` })
    .then(() => { toast.success("Agendamento salvo."); carregar(); }).catch((e) => toast.error(e.message));
  const disparar = () => {
    if (!window.confirm("Rodar o download agora? Entra no portal Brasíndice e baixa os arquivos que faltam.")) return;
    brasindiceAPI.disparar().then(() => { toast.success("Download disparado."); setTimeout(carregar, 800); }).catch((e) => toast.error(e.message));
  };
  const r = ag?.ultimo_run; const x = auto?.automacao;
  const K = { fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".04em", color: T.inkSoft, fontWeight: 600, flex: "0 0 170px", paddingTop: 4 };
  const KV = ({ k, children }) => <div style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.line}`, alignItems: "flex-start" }}><div style={K}>{k}</div><div style={{ fontSize: 14 }}>{children}</div></div>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 16 }}>
      <Card>
        <div style={{ fontWeight: 700, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}><CalendarDays size={18} color={T.teal} /> Quando roda sozinho</div>
        <KV k="Ligado"><label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: admin ? "pointer" : "default" }}><input type="checkbox" checked={ativa} disabled={!admin} onChange={(e) => setAtiva(e.target.checked)} /> {ativa ? "sim" : "não (só pelo botão Disparar agora)"}</label></KV>
        <KV k="Dias do mês">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxWidth: 420 }} title={admin ? "O Brasíndice publica uma edição nova em datas fixas: dias 5 e 20. Clique nos dias para marcar ou desmarcar." : "Só administrador do AURA altera os dias."}>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
              <button key={d} type="button" disabled={!admin} onClick={() => setDias((s) => { const n = new Set(s); if (n.has(d)) n.delete(d); else n.add(d); return n; })}
                style={{ width: 40, height: 34, borderRadius: 6, border: `1px solid ${dias.has(d) ? T.teal : T.line}`, background: dias.has(d) ? T.teal : "#fff", color: dias.has(d) ? "#fff" : T.ink, fontWeight: dias.has(d) ? 700 : 400, cursor: admin ? "pointer" : "default", opacity: admin || dias.has(d) ? 1 : 0.5 }}>{d}</button>))}
          </div>
        </KV>
        <KV k="Hora">
          <select value={hh} disabled={!admin} onChange={(e) => setHh(e.target.value)} style={{ padding: "6px 8px", borderRadius: 6, border: `1px solid ${T.line}` }}>{Array.from({ length: 24 }, (_, i) => pad(i)).map((h) => <option key={h}>{h}</option>)}</select>
          <span style={{ margin: "0 6px" }}>:</span>
          <select value={mm} disabled={!admin} onChange={(e) => setMm(e.target.value)} style={{ padding: "6px 8px", borderRadius: 6, border: `1px solid ${T.line}` }}>{["00", "15", "30", "45"].concat(["00", "15", "30", "45"].includes(mm) ? [] : [mm]).map((m) => <option key={m}>{m}</option>)}</select>
        </KV>
        <KV k="Próxima execução">{ag ? (ag.ativa ? <b style={{ color: T.tealDark }}>{fmtExtenso(ag.proxima_execucao)}</b> : <span style={{ color: T.inkSoft }}>agendamento desligado</span>) : "—"}</KV>
        <KV k="Último disparo">{ag?.ultimo_disparo ? <>{fmtData(ag.ultimo_disparo)} <span style={{ color: T.inkSoft, fontSize: 12.5 }}>({ag.ultimo_motivo === "agenda" ? "pelo agendamento" : "manual"})</span></> : <span style={{ color: T.inkSoft }}>nenhum ainda</span>}</KV>
        {admin
          ? <div style={{ marginTop: 14 }}><Botao onClick={salvar}>Salvar</Botao></div>
          : <div style={{ marginTop: 14, fontSize: 13, color: T.inkSoft }}><Lock size={13} style={{ verticalAlign: "text-bottom" }} /> Só administrador do AURA altera o agendamento. Você pode ver e disparar.</div>}
        {ag && <div style={{ fontSize: 12.5, color: T.inkSoft, marginTop: 12 }}>{ag.laco_ativo ? "Agendamento ativo enquanto o módulo estiver no ar." : "Agendador interno do módulo parado."}</div>}
      </Card>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Card>
          <div style={{ fontWeight: 700, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}><Play size={18} color={T.teal} /> Rodar agora</div>
          <p style={{ margin: "0 0 12px", fontSize: 14 }}>Entra no portal, descobre a edição atual, baixa os arquivos que faltam e testa cada um no Oracle de simulação. Leva de 2 a 15 minutos.</p>
          <Botao cor={T.verde} onClick={disparar} disabled={!!ag?.processo_ativo}><Play size={15} /> Disparar agora</Botao>
          <div style={{ fontSize: 13, color: T.inkSoft, marginTop: 10 }}>
            {ag?.processo_ativo ? <span><RefreshCw size={13} /> Download rodando desde {fmtData(ag.processo_ativo.inicio)}{ag.processo_ativo.motivo === "agenda" ? " (pelo agendamento)" : ""}…</span>
              : r ? <span>Última execução: <b>{fmtData(r.inicio)}</b> · {r.status === "success" ? <span style={{ color: T.verde }}>concluída</span> : r.status === "skipped" ? "nada a baixar" : r.status} · {r.ok} baixado(s), {r.skip} já existiam, {r.erro} erro(s) · {r.duracao_fmt}</span>
              : "Nenhuma execução registrada ainda."}
          </div>
        </Card>
        <Card>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Depois do download</div>
          <div style={{ fontSize: 13.5, color: T.inkSoft }}>
            {!auto ? "—" : x?.pos_download_ativo
              ? <>
                <div><CheckCircle2 size={13} color={T.verde} /> Automático: {[x.validar ? "valida o layout" : null, x.gerar_sql ? "gera o SQL" : null, x.testar_oracle ? "testa no Oracle de simulação com rollback" : null].filter(Boolean).join(", ")}.</div>
                <div style={{ marginTop: 4 }}>{x.gravar ? "Grava sozinho depois do teste." : "Gravar no Soul é sempre o clique \"Importar edição\" em Edições."}</div>
              </>
              : "Nada acontece sozinho depois do download."}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ── aba Dashboard ────────────────────────────────────────────────────── */
const NIVEL = {
  risco: { cor: T.risco, fundo: T.riscoSoft, rot: "Risco" },
  atencao: { cor: T.ambar, fundo: T.ambarSoft, rot: "Atenção" },
  info: { cor: T.azul, fundo: T.azulSoft, rot: "Info" },
};
const fmtPct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`);
const fmtDur = (s) => (s == null ? "—" : s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`);
const Secao = ({ titulo, sub, acao, children }) => (
  <Card style={{ marginBottom: 14 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 16, color: T.ink }}>{titulo}</h2>
        {sub && <div style={{ color: T.inkSoft, fontSize: 13, marginTop: 2 }}>{sub}</div>}
      </div>
      {acao}
    </div>
    {children}
  </Card>
);
const Numero = ({ titulo, valor, sub, cor = T.ink, dica }) => (
  <div title={dica} style={{ padding: "12px 14px", border: `1px solid ${T.line}`, borderRadius: 10, minWidth: 0, cursor: dica ? "help" : "default" }}>
    <div style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".04em", color: T.inkSoft, fontWeight: 600 }}>{titulo}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: cor, marginTop: 2, lineHeight: 1.2 }}>{valor}</div>
    {sub && <div style={{ fontSize: 12.5, color: T.inkSoft, marginTop: 2 }}>{sub}</div>}
  </div>
);
const Detalhes = ({ titulo, qtd, children }) => (
  <details style={{ marginTop: 10, border: `1px solid ${T.line}`, borderRadius: 10, padding: "8px 12px" }}>
    <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: 13.5, color: T.tealDark }}>{titulo}{qtd != null ? <span style={{ color: T.inkSoft, fontWeight: 400 }}> · {fmtN(qtd)}</span> : null}</summary>
    <div style={{ marginTop: 8, maxHeight: 360, overflow: "auto" }}>{children}</div>
  </details>
);
const TabelaItens = ({ linhas, colunas }) => (
  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
    <thead><tr>{colunas.map((c) => <th key={c.k} style={{ ...TH, textAlign: c.dir || "left" }}>{c.t}</th>)}</tr></thead>
    <tbody>{linhas.map((l, i) => <tr key={i}>{colunas.map((c) => <td key={c.k} style={{ ...TD, padding: "7px 12px", textAlign: c.dir || "left", fontFamily: c.mono ? MONO : undefined }}>{c.f ? c.f(l) : (l[c.k] ?? "—")}</td>)}</tr>)}</tbody>
  </table>
);

function Dashboard({ admin, onIrPara }) {
  const [alertas, setAlertas] = useState(null);
  const [lista, setLista] = useState(null);
  const [edicao, setEdicao] = useState(null);
  const [an, setAn] = useState(null);
  const [op, setOp] = useState(null);
  const [baixando, setBaixando] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    brasindiceAPI.alertas().then(setAlertas).catch(() => setAlertas({ alertas: [] }));
    brasindiceAPI.operacao().then(setOp).catch(() => setOp(null));
    brasindiceAPI.analiseLista().then((l) => { setLista(l); if (l.atual) setEdicao(l.atual); }).catch(() => setLista({ edicoes: [] }));
  }, []);

  const carregarAnalise = useCallback((ed, recalcular = false) => {
    if (!ed) return;
    brasindiceAPI.analise(ed, recalcular).then((r) => {
      setAn(r);
      if (timer.current) clearTimeout(timer.current);
      if (r.calculando) timer.current = setTimeout(() => carregarAnalise(ed), 5000);
      else brasindiceAPI.alertas().then(setAlertas).catch(() => {});
    }).catch((e) => { toast.error(e.message); setAn({ erro: e.message }); });
  }, []);

  useEffect(() => { setAn(null); carregarAnalise(edicao); return () => { if (timer.current) clearTimeout(timer.current); }; }, [edicao, carregarAnalise]);

  const t = an?.totais;
  const saude = an?.saude_cadastro;
  const relatorio = () => {
    setBaixando(true);
    brasindiceAPI.relatorioTiss(edicao).catch((e) => toast.error(e.message)).finally(() => setBaixando(false));
  };

  return (
    <>
      <Secao titulo="Alertas" sub="O que precisa de olho humano agora. Some sozinho quando a causa é resolvida.">
        {!alertas && <div style={{ color: T.inkSoft, fontSize: 13.5 }}>Carregando…</div>}
        {alertas && !alertas.alertas.length && <div style={{ color: T.verde, fontSize: 14, display: "flex", gap: 6, alignItems: "center" }}><CheckCircle2 size={16} /> Nada pendente: agendamento em dia, Soul respondendo e nenhuma edição com problema.</div>}
        {alertas?.alertas.map((a, i) => {
          const n = NIVEL[a.nivel] || NIVEL.info;
          return (
            <div key={i} onClick={() => a.aba && a.aba !== "dashboard" && onIrPara(a.aba)}
              style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 12px", borderLeft: `3px solid ${n.cor}`, background: n.fundo, borderRadius: 8, marginBottom: 6, cursor: a.aba && a.aba !== "dashboard" ? "pointer" : "default" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: n.cor, textTransform: "uppercase", letterSpacing: ".04em", marginTop: 3, minWidth: 56 }}>{n.rot}</span>
              <div><div style={{ fontWeight: 600, fontSize: 14, color: T.ink }}>{a.titulo}</div>{a.detalhe && <div style={{ fontSize: 13, color: T.inkSoft }}>{a.detalhe}</div>}</div>
            </div>
          );
        })}
      </Secao>

      <Secao titulo="Impacto da edição no Soul"
        sub="Antes de gravar: quantos itens da edição encontram procedimento no Soul (pelas mesmas vias da carga) e o que acontece com o preço."
        acao={(
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <select value={edicao || ""} onChange={(e) => setEdicao(e.target.value)} style={{ padding: "7px 10px", borderRadius: 8, border: `1px solid ${T.line}`, fontSize: 14 }}>
              {(lista?.edicoes || []).map((e) => <option key={e} value={e}>Edição {e}</option>)}
            </select>
            {admin && <Botao sec disabled={!edicao || !!an?.calculando} onClick={() => carregarAnalise(edicao, true)} titulo="Refaz a leitura dos arquivos e a consulta ao Soul (alguns minutos)."><RefreshCw size={14} /> Recalcular</Botao>}
          </div>
        )}>
        {lista && !lista.edicoes.length && <div style={{ color: T.inkSoft }}>Nenhuma edição baixada ainda.</div>}
        {an?.calculando && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", color: T.inkSoft, fontSize: 14, padding: "8px 0" }}>
            <Clock size={16} /> Calculando a edição {an.edicao}: {an.fase || "iniciando"}… A tela atualiza sozinha; leva alguns minutos na primeira vez.
          </div>
        )}
        {an?.erro && <div style={{ color: T.risco, fontSize: 14, padding: "8px 0" }}><AlertTriangle size={15} /> Não deu para analisar: {an.erro}{an.desatualizado ? " (mostrando o último resultado bom abaixo)" : ""}</div>}
        {(() => {
          const d = an?.totais ? an : an?.desatualizado;
          if (!d?.totais) return null;
          const tt = d.totais;
          const cats = d.por_categoria || [];
          return (
            <>
              <div style={{ fontSize: 12.5, color: T.inkSoft, marginBottom: 10 }}>
                Edição {d.edicao}{d.publicada ? `, publicada em ${fmtDia(d.publicada)}` : ""} · {d.arquivos_lidos.length} de 9 arquivos no disco · calculado em {fmtData(d.calculado_em)} ({fmtDur(d.duracao_s)}){d.edicao_anterior ? ` · comparado com a ${d.edicao_anterior}` : ""}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 12 }}>
                <Numero titulo="Itens na edição" valor={fmtN(tt.itens)} sub={`${fmtN(tt.sem_tuss)} sem TUSS`} dica="Linhas com código TISS nos arquivos baixados. Sem TUSS é normal em materiais e produtos novos." />
                <Numero titulo="Recebem preço" valor={fmtN(tt.localizados)} cor={T.verde} sub={tt.multiplos ? `${fmtN(tt.multiplos)} caem em mais de um procedimento` : "cada um em um procedimento"} dica="Itens que a carga localiza no Soul: TISS no grupo do tipo, TUSS como código ou TUSS em CD_TISS, nunca SIMPRO." />
                <Numero titulo="Sem procedimento no Soul" valor={fmtN(tt.sem_destino)} cor={tt.sem_destino ? T.ambar : T.verde} sub="não recebem preço" dica="A carga não cria procedimento (regra do Soul). Estes itens ficam registrados no bruto e sem valor de referência." />
                <Numero titulo="Preço sobe" valor={fmtN(tt.preco_sobe)} cor={T.risco} dica="Preço unitário maior que o último VALOR_REF_PROC do procedimento." />
                <Numero titulo="Preço desce" valor={fmtN(tt.preco_desce)} cor={T.verde} dica="Preço unitário menor que o último VALOR_REF_PROC." />
                <Numero titulo="Sem mudança" valor={fmtN(tt.preco_igual)} sub={`${fmtN(tt.sem_preco_anterior)} sem preço anterior`} dica="Mesmo preço da última carga. Sem preço anterior: procedimento nunca recebeu valor desta tabela." />
                {d.edicao_anterior && <Numero titulo="Saíram da lista" valor={fmtN(tt.sairam)} sub={`estavam na ${d.edicao_anterior}`} dica="Itens presentes no arquivo da edição anterior e ausentes nesta. Continuam no Soul com o último preço." />}
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead><tr>
                  <th style={TH}>Arquivo</th><th style={{ ...TH, textAlign: "right" }}>Itens</th><th style={{ ...TH, textAlign: "right" }}>Sem TUSS</th>
                  <th style={{ ...TH, textAlign: "right" }}>Recebem preço</th><th style={{ ...TH, textAlign: "right" }}>Sem procedimento</th>
                  <th style={{ ...TH, textAlign: "right" }}>Sobe</th><th style={{ ...TH, textAlign: "right" }}>Desce</th><th style={{ ...TH, textAlign: "right" }}>Igual</th>
                  <th style={{ ...TH, textAlign: "right" }} title="Variação média do preço unitário entre o último valor no Soul e o desta edição.">Var. média</th>
                  <th style={{ ...TH, textAlign: "right" }}>Saíram</th>
                </tr></thead>
                <tbody>
                  {cats.map((c) => (
                    <tr key={c.categoria}>
                      <td style={{ ...TD, fontWeight: 600 }}>{c.categoria}</td>
                      <td style={{ ...TD, textAlign: "right" }}>{fmtN(c.itens)}</td>
                      <td style={{ ...TD, textAlign: "right", color: T.inkSoft }}>{fmtN(c.sem_tuss)}</td>
                      <td style={{ ...TD, textAlign: "right", color: T.verde }}>{fmtN(c.localizados)}</td>
                      <td style={{ ...TD, textAlign: "right", color: c.sem_destino ? T.ambar : T.inkSoft }}>{fmtN(c.sem_destino)}</td>
                      <td style={{ ...TD, textAlign: "right" }}>{fmtN(c.preco_sobe)}</td>
                      <td style={{ ...TD, textAlign: "right" }}>{fmtN(c.preco_desce)}</td>
                      <td style={{ ...TD, textAlign: "right", color: T.inkSoft }}>{fmtN(c.preco_igual)}</td>
                      <td style={{ ...TD, textAlign: "right", fontWeight: 600, color: c.variacao_media_pct > 0 ? T.risco : c.variacao_media_pct < 0 ? T.verde : T.ink }}>{fmtPct(c.variacao_media_pct)}</td>
                      <td style={{ ...TD, textAlign: "right", color: T.inkSoft }}>{c.sairam == null ? "—" : fmtN(c.sairam)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Detalhes titulo="Itens sem procedimento no Soul" qtd={tt.sem_destino}>
                {d.sem_destino.length < tt.sem_destino && <div style={{ fontSize: 12.5, color: T.inkSoft, marginBottom: 6 }}>Mostrando os primeiros {fmtN(d.sem_destino.length)}. A lista completa sai na pesquisa por item.</div>}
                <TabelaItens linhas={d.sem_destino} colunas={[
                  { k: "tiss", t: "TISS", mono: true }, { k: "tuss", t: "TUSS", mono: true, f: (l) => l.tuss || <span style={{ color: T.inkSoft }}>sem TUSS</span> },
                  { k: "descricao", t: "Produto" }, { k: "apresentacao", t: "Apresentação" }, { k: "laboratorio", t: "Laboratório" },
                  { k: "categoria", t: "Arquivo" }, { k: "valor", t: "Unitário", dir: "right", f: (l) => fmtR(l.valor) }]} />
              </Detalhes>
              <Detalhes titulo="Maiores altas de preço" qtd={d.maiores_altas.length}>
                <TabelaItens linhas={d.maiores_altas} colunas={[
                  { k: "tiss", t: "TISS", mono: true }, { k: "descricao", t: "Produto" }, { k: "laboratorio", t: "Laboratório" }, { k: "categoria", t: "Arquivo" },
                  { k: "de", t: "De", dir: "right", f: (l) => fmtR(l.de) }, { k: "para", t: "Para", dir: "right", f: (l) => fmtR(l.para) },
                  { k: "pct", t: "Variação", dir: "right", f: (l) => <b style={{ color: T.risco }}>{fmtPct(l.pct)}</b> }, { k: "edicao_anterior", t: "Última edição", f: (l) => l.edicao_anterior || "—" }]} />
              </Detalhes>
              <Detalhes titulo="Maiores quedas de preço" qtd={d.maiores_quedas.length}>
                <TabelaItens linhas={d.maiores_quedas} colunas={[
                  { k: "tiss", t: "TISS", mono: true }, { k: "descricao", t: "Produto" }, { k: "laboratorio", t: "Laboratório" }, { k: "categoria", t: "Arquivo" },
                  { k: "de", t: "De", dir: "right", f: (l) => fmtR(l.de) }, { k: "para", t: "Para", dir: "right", f: (l) => fmtR(l.para) },
                  { k: "pct", t: "Variação", dir: "right", f: (l) => <b style={{ color: T.verde }}>{fmtPct(l.pct)}</b> }, { k: "edicao_anterior", t: "Última edição", f: (l) => l.edicao_anterior || "—" }]} />
              </Detalhes>
              {tt.multiplos > 0 && (
                <Detalhes titulo="Itens que caem em mais de um procedimento" qtd={tt.multiplos}>
                  <div style={{ fontSize: 12.5, color: T.inkSoft, marginBottom: 6 }}>O mesmo item atualiza todos estes procedimentos (é assim que o Soul faz). Vale conferir se são o mesmo produto.</div>
                  <TabelaItens linhas={d.multiplos} colunas={[
                    { k: "tiss", t: "TISS", mono: true }, { k: "tuss", t: "TUSS", mono: true }, { k: "descricao", t: "Produto" }, { k: "categoria", t: "Arquivo" },
                    { k: "procedimentos", t: "Procedimentos no Soul", f: (l) => l.procedimentos.join(" · ") }]} />
                </Detalhes>
              )}
            </>
          );
        })()}
      </Secao>

      <Secao titulo="Saúde do cadastro do Soul"
        sub="Códigos do Brasíndice que caem em procedimento errado ou ambíguo no Soul. A carga se protege (ignora SIMPRO), mas o cadastro precisa de correção pelo operador."
        acao={edicao && <Botao sec disabled={baixando} onClick={relatorio} titulo="Gera a planilha com as listas completas para enviar ao operador do Soul. Consulta o Soul na hora."><Download size={14} /> {baixando ? "Gerando…" : "Relatório para o operador (xlsx)"}</Botao>}>
        {!saude && !an?.desatualizado?.saude_cadastro && <div style={{ color: T.inkSoft, fontSize: 13.5 }}>{an?.calculando ? "Aguardando a análise da edição…" : "Escolha uma edição analisada."}</div>}
        {(() => {
          const s = saude || an?.desatualizado?.saude_cadastro;
          if (!s) return null;
          const ROT = { colisoes: "Colisões", tiss_errado: "TISS na coluna do TUSS", tuss_mudou: "TUSS mudou", sem_tuss_no_soul: "Sem TUSS no Soul" };
          const COR = { colisoes: T.risco, tiss_errado: T.ambar, tuss_mudou: T.azul, sem_tuss_no_soul: T.inkSoft };
          return (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 6 }}>
                <Numero titulo="Itens da edição no cadastro" valor={fmtN(s.itens_no_cadastro)} dica="Códigos TISS da edição que existem como CD_PROCEDIMENTO no Soul." />
                {Object.keys(ROT).map((k) => <Numero key={k} titulo={ROT[k]} valor={fmtN(s.contagens[k])} cor={s.contagens[k] ? COR[k] : T.verde} dica={s.explicacao[k]} />)}
              </div>
              {Object.keys(ROT).filter((k) => s.amostras[k]?.length).map((k) => (
                <Detalhes key={k} titulo={`${ROT[k]}: ${s.explicacao[k]}`} qtd={s.contagens[k]}>
                  {s.amostras[k].length < s.contagens[k] && <div style={{ fontSize: 12.5, color: T.inkSoft, marginBottom: 6 }}>Primeiros {s.amostras[k].length}; a lista completa vai no relatório.</div>}
                  <TabelaItens linhas={s.amostras[k]} colunas={[
                    { k: "tiss", t: "Procedimento", mono: true }, { k: "soul", t: "No Soul" }, { k: "cd_tiss_soul", t: "CD_TISS no Soul", mono: true }, { k: "codificacao", t: "Codif." },
                    { k: "brasindice", t: "No Brasíndice" }, { k: "tuss", t: "TUSS Brasíndice", mono: true }, { k: "arquivo", t: "Arquivo" }]} />
                </Detalhes>
              ))}
            </>
          );
        })()}
      </Secao>

      <Secao titulo="Operação" sub="Últimos downloads do portal e últimas execuções no Soul (teste com rollback e gravação).">
        {!op && <div style={{ color: T.inkSoft, fontSize: 13.5 }}>Carregando…</div>}
        {op && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 16 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 4 }}>Downloads</div>
              {!op.downloads.length && <div style={{ color: T.inkSoft, fontSize: 13 }}>Nenhum ainda.</div>}
              {op.downloads.length > 0 && <TabelaItens linhas={op.downloads} colunas={[
                { k: "inicio", t: "Quando", f: (l) => fmtData(l.inicio) }, { k: "edicao", t: "Edição", f: (l) => l.edicao || "—" },
                { k: "status", t: "Resultado", f: (l) => <span style={{ color: l.status === "success" ? T.verde : l.status === "running" ? T.azul : l.status === "skipped" ? T.inkSoft : T.risco, fontWeight: 600 }}>{{ success: "concluído", running: "rodando", skipped: "nada a baixar", partial: "parcial", failed: "falhou" }[l.status] || l.status}</span> },
                { k: "ok", t: "Arquivos", f: (l) => `${l.ok} novos · ${l.skip} já existiam${l.erro ? ` · ${l.erro} erro(s)` : ""}` }, { k: "duracao_s", t: "Duração", dir: "right", f: (l) => fmtDur(l.duracao_s) }]} />}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 4 }}>Execuções no Soul</div>
              {!op.execucoes.length && <div style={{ color: T.inkSoft, fontSize: 13 }}>Nenhuma ainda.</div>}
              {op.execucoes.length > 0 && <TabelaItens linhas={op.execucoes} colunas={[
                { k: "inicio", t: "Quando", f: (l) => fmtData(l.inicio) }, { k: "edicao", t: "Edição" }, { k: "categoria", t: "Arquivo" },
                { k: "modo", t: "Modo", f: (l) => (l.modo === "commit" ? <b style={{ color: T.tealDark }}>gravação</b> : "teste") },
                { k: "ok", t: "Resultado", f: (l) => (l.ok ? <span style={{ color: T.verde, fontWeight: 600 }}>ok</span> : <span style={{ color: T.risco, fontWeight: 600 }} title={l.erro || ""}>falhou</span>) },
                { k: "comandos", t: "Comandos", dir: "right", f: (l) => fmtN(l.comandos) }, { k: "duracao_s", t: "Duração", dir: "right", f: (l) => fmtDur(l.duracao_s) },
                { k: "log_id", t: "Carga", mono: true, f: (l) => l.log_id || "—" }]} />}
            </div>
          </div>
        )}
      </Secao>
    </>
  );
}

/* ── página ───────────────────────────────────────────────────────────── */
export default function ImportacaoBrasindice() {
  const [aba, setAba] = useState("edicoes");
  const [servico, setServico] = useState(null);
  const [erro, setErro] = useState(null);
  const [dados, setDados] = useState(null);
  const [abertas, setAbertas] = useState(new Set());
  const [busca, setBusca] = useState("");
  const [termo, setTermo] = useState("");
  const [progresso, setProgresso] = useState({});
  const [historico, setHistorico] = useState(null);
  const [me, setMe] = useState(null);
  const [painel, setPainel] = useState(null);
  const polls = useRef({});

  const carregar = useCallback(() => {
    setErro(null);
    brasindiceAPI.edicoes().then(setDados).catch((e) => setErro(e.message));
    brasindiceAPI.painel().then(setPainel).catch(() => setPainel(null));
    brasindiceAPI.me().then(setMe).catch(() => setMe(null));
  }, []);

  useEffect(() => {
    servicoBrasindice().then((s) => { setServico(s); if (s.configurado) carregar(); }).catch((e) => setErro(e?.response?.data?.detail || e.message));
    const p = polls.current;
    return () => Object.values(p).forEach(clearInterval);
  }, [carregar]);

  const acompanhar = (edicao) => {
    if (polls.current[edicao]) clearInterval(polls.current[edicao]);
    polls.current[edicao] = setInterval(async () => {
      try {
        const st = await brasindiceAPI.statusImportacao(edicao);
        setProgresso((p) => ({ ...p, [edicao]: st }));
        if (!st.inexistente && !st.em_andamento) {
          clearInterval(polls.current[edicao]); delete polls.current[edicao];
          if (st.ok) toast.success(`Edição ${edicao} importada: ${st.gravados} arquivos gravados.`); else toast.error(`Edição ${edicao}: ${st.falhas} arquivo(s) falharam.`);
          carregar();
        }
      } catch (e) { clearInterval(polls.current[edicao]); delete polls.current[edicao]; }
    }, 2000);
  };
  const importar = (ed) => {
    const n = ed.arquivos.filter((a) => a.existe && a.status !== "importado" && a.status !== "erro").length;
    if (!window.confirm(`Importar a edição ${ed.edicao} no Soul de SIMULAÇÃO?\n\n${n} arquivo(s) serão testados e, se passarem, gravados. Pode levar alguns minutos.`)) return;
    brasindiceAPI.importarEdicao(ed.edicao).then(() => { toast.success("Importação iniciada."); acompanhar(ed.edicao); }).catch((e) => toast.error(e.message));
  };
  const toggle = (ed) => setAbertas((s) => { const n = new Set(s); n.has(ed) ? n.delete(ed) : n.add(ed); return n; });
  const pesquisar = (e) => { e.preventDefault(); const q = busca.trim(); if (q.length < 3) { toast.error("Digite ao menos 3 caracteres."); return; } setTermo(q); };

  const Aba = ({ id, icone, label }) => (
    <button onClick={() => setAba(id)} style={{ background: aba === id ? T.tealSoft : "transparent", color: aba === id ? T.tealDark : T.inkSoft, border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 600, cursor: "pointer", display: "inline-flex", gap: 6, alignItems: "center", fontSize: 14 }}>{icone}{label}</button>
  );

  return (
    <div style={{ padding: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Pill size={26} color={T.teal} />
          <div>
            <h1 style={{ margin: 0, fontSize: 22, color: T.ink }}>Importação Brasíndice</h1>
            <div style={{ color: T.inkSoft, fontSize: 13.5 }}>Edições de preços do Brasíndice: download, conferência e gravação no Soul.</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Aba id="edicoes" icone={<Database size={15} />} label="Edições" />
          <Aba id="agendamento" icone={<CalendarDays size={15} />} label="Agendamento" />
          <Aba id="dashboard" icone={<LayoutDashboard size={15} />} label="Dashboard" />
        </div>
      </div>

      {erro && <Card style={{ borderLeft: `3px solid ${T.risco}`, marginBottom: 12 }}><AlertTriangle size={15} color={T.risco} /> {erro}</Card>}
      {servico && !servico.configurado && <Card style={{ borderLeft: `3px solid ${T.ambar}` }}>{servico.aviso}</Card>}

      {servico?.configurado && aba === "agendamento" && <Agendamento admin={!!me?.admin} />}
      {servico?.configurado && aba === "dashboard" && <Dashboard admin={!!me?.admin} onIrPara={setAba} />}

      {servico?.configurado && aba === "edicoes" && (
        <>
          <Painel p={painel} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <div style={{ color: T.inkSoft, fontSize: 13.5 }}>Uma linha por edição. Clique para ver os 9 arquivos e importar.</div>
            <form onSubmit={pesquisar} style={{ display: "flex", gap: 6 }}>
              <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Pesquisar item: TISS, TUSS, EAN ou nome"
                title="Número: procura o código exato (zeros à esquerda não importam). Texto: procura no nome, na apresentação e no laboratório. Lê todos os arquivos baixados."
                style={{ width: 360, maxWidth: "60vw", padding: "8px 12px", borderRadius: 8, border: `1px solid ${T.line}`, fontSize: 14 }} />
              <Botao onClick={pesquisar}><Search size={15} /></Botao>
              <Botao sec onClick={(e) => { e.preventDefault(); carregar(); }} titulo="Recarrega a lista lendo o módulo e as cargas do Soul."><RefreshCw size={15} /></Botao>
            </form>
          </div>

          {dados?.soul_indisponivel && <Card style={{ borderLeft: `3px solid ${T.ambar}`, marginBottom: 12, fontSize: 13.5 }}>Sem resposta do Soul agora ({dados.soul_indisponivel}). A lista mostra só o que está no módulo.</Card>}
          {dados && !dados.oracle_habilitado && <Card style={{ borderLeft: `3px solid ${T.ambar}`, marginBottom: 12, fontSize: 13.5 }}>A execução no Oracle está desligada no módulo. Importar fica bloqueado.</Card>}

          {termo ? <Pesquisa termo={termo} onVoltar={() => { setTermo(""); setBusca(""); }} /> : (
            <Card style={{ padding: 0, overflow: "hidden" }}>
              {!dados && !erro && <div style={{ padding: 24, color: T.inkSoft }}>Carregando edições…</div>}
              {dados && (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead><tr>
                    <th style={TH} title="Número da edição do Brasíndice. Cada edição traz os 9 arquivos de preços.">Edição</th>
                    <th style={TH} title="Data de publicação lida na página Edições do portal. É a vigência dos preços no Soul.">Publicada</th>
                    <th style={TH} title="Quantos dos 9 arquivos estão baixados e quantos já estão gravados no Soul.">Arquivos</th>
                    <th style={TH} title="Importado: os 9 arquivos no Soul. Parcial: alguns. Pendente: nenhum.">Situação</th>
                    <th style={TH} />
                  </tr></thead>
                  <tbody>
                    {!dados.edicoes.length && <tr><td colSpan={5} style={{ ...TD, textAlign: "center", color: T.inkSoft, padding: 28 }}>Nenhuma edição ainda. Use Disparar agora em Agendamento.</td></tr>}
                    {dados.edicoes.map((ed) => {
                      const aberta = abertas.has(ed.edicao); const s = SIT[ed.situacao] || SIT.pendente;
                      return (
                        <React.Fragment key={ed.edicao}>
                          <tr onClick={() => toggle(ed.edicao)} style={{ cursor: "pointer", background: aberta ? T.tealSoft : "#fff" }}>
                            <td style={{ ...TD, fontWeight: 700, fontSize: 18, color: T.tealDark }}>{ed.edicao}</td>
                            <td style={TD}>{ed.publicada ? fmtDia(ed.publicada) : <span style={{ color: T.inkSoft }}>—</span>}</td>
                            <td style={TD}>{ed.baixados} de {ed.total} baixados <span style={{ color: T.inkSoft }}>· {ed.importados} importado{ed.importados === 1 ? "" : "s"}{ed.problemas ? <span style={{ color: T.risco }}> · {ed.problemas} com problema</span> : null}</span></td>
                            <td style={TD}><Selo {...s} /></td>
                            <td style={{ ...TD, textAlign: "right", color: T.inkSoft }}>{aberta ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</td>
                          </tr>
                          {aberta && (
                            <tr><td colSpan={5} style={{ background: T.tealSoft, padding: 0 }}>
                              <DetalheEdicao ed={ed} oracleOn={!!dados.oracle_habilitado} onImportar={importar} progresso={progresso[ed.edicao]} onHistorico={setHistorico} />
                            </td></tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </Card>
          )}
        </>
      )}

      {historico && <HistoricoArquivo categoria={historico} onFechar={() => setHistorico(null)} />}
    </div>
  );
}
